export class Messaging {
	_wsUrl
	_subscriptions = new Map()
	_paused = false
	_waitForConnectionTime = 5 * 60 * 1000
	_checkHeartbeatTimeout = null
	_lastHeartbeat
	_connecting = false
	_reconnecting = false
	_refreshHeartbeatInterval


	constructor() {
		let port = 2080
		if (window.location.hostname !== 'localhost') {
			port = window.location.port
		}
		this._wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}:${port}/ws`
	}

	setGetValidTokenPromise = getValidTokenPromise => this._getValidTokenPromise = getValidTokenPromise

	/**
	 * 
	 * @param {*} onPause Called when the connection is broken
	 * @param {*} onResume Called when the connection is reestablished
	 * @returns A Promise that resolves to true when connect event on success
	 */
	connect = (onPause, onResume) => {
		return new Promise((resolve, reject) => {
			this._closeIntended = false
			const timeout = new Date().getTime() + this._waitForConnectionTime
			this._connect(timeout, onPause, onResume)
				.then(() => {
					// Start checking for heartbeat
					this._lastHeartbeat = new Date().getTime()
					this._checkHeratbeat(onPause, onResume)
					// Take care that the heartbeat surveilence does not stop
					this._refreshHeartbeatInterval = setInterval(() => this._checkHeratbeat(onPause, onResume), 15 * 60 * 1000)

					resolve(true)
				})
				.catch(e => {
					console.error('Error received!', e)
					reject(e)
				})
		})
	}

	/**
	 * Intended with no reconnect
	 */
	close = () => {
		this._closeIntended = true
		clearTimeout(this._checkHeartbeatTimeout)
		clearInterval(this._refreshHeartbeatInterval)
		if (this._webSocket) {
			// Unsubscribe
			this._subscriptions.forEach((callback, topic) => {
				this._webSocket.send(`{"topic": "${topic}", "subscribe": false}`)
			})
			this._subscriptions.clear()
			// Disconnect
			this._webSocket.close(1000, 'CLOSE')
			this._webSocket = null;
		}
	}

	/**
	 * 
	 * @param {*} topic 
	 * @param {*} onMessage Called with parsed Object as argument
	 */
	subscribe = (topic, onMessage) => {
		this._subscriptions.set(topic, onMessage)
		// Request for subscriptions on server
		this._webSocket.send(`{"topic": "${topic}", "subscribe": true}`)
	}

	/**
	 * 
	 * @param {*} topic 
	 * @param {*} subscriptionRequestKey Security token String
	 * @param {*} onMessage 
	 */
	subscribeWithToken = (topic, token, onMessage) => {
		this._subscriptions.set(topic, onMessage)
		// Request for subscriptions on server
		this._webSocket.send(`{"topic": "${topic}", "subscribe": true, "token": "${token}"}`)
	}

	getSubscriptions = () => Array.from(this._subscriptions.keys())

	unSubscribe = topic => {
		this._webSocket.send(`{"topic": "${topic}", "subscribe": false}`)
		this._subscriptions.delete(topic)
	}

	publish = (topic, content) => {
		const message = { topic: topic, content: content }
		this._webSocket.send(JSON.stringify(message))
	}

	/**
	 * Create a connection and wait for the connet event
	 * 
	 * @param {*} timeout 
	 * @param {*} onPause 
	 * @param {*} onResume 
	 * @returns A Promise tha resolves when a new WebSocket is created and the open event has occured
	 */
	_connect = (timeout, onPause, onResume) => {
		return new Promise((resolve, reject) => {
			if (!this._connecting) {
				this._connecting = true
				this._newWebSocket(timeout)
					.then(() => {
						// OnError when connected
						this._webSocket.onerror = event => {
							// Connection dropped with error
							console.debug('Error on WebSocket', event)
							this._webSocket = null;				
							if (!this._closeIntended) {
								this._autoReconnect(onPause, onResume)
								this._lastHeartbeat = 0
								this._checkHeratbeat(onPause, onResume)
							}
						}
						this._webSocket.onclose = event => {
							// Connection closed
							console.debug(`WebSocket has been closed by ${event.code === 1000 ? `Client said ${event.reason}` : event.code === 1001 ? `Server said ${event.reason}` : 'Client or Server'}`, event)
							this._webSocket = null;
							if (!this._closeIntended) {
								this._autoReconnect(onPause, onResume)
								this._lastHeartbeat = 0
								this._checkHeratbeat(onPause, onResume)
							}
						}
						// On message
						this._webSocket.onmessage = message => {
							this._onMessage(message, onPause, onResume)
						}
						this._connecting = false
						resolve()
					})
					.catch(e => {
						console.info('Failed to create a new WebSocket!', e)
						this._connecting = false
						reject(e)
					})
			}
			else {
				reject('Already connecting!')
			}
		})
	}

	_newWebSocket = timeout => {
		return new Promise((resolve, reject) => this.__newWebSocket(resolve, reject, timeout))
	}

	/**
	 * Calls itself until a new WebSocket is created or a timeout occurs
	 * 
	 * @param {*} resolve 
	 * @param {*} reject 
	 * @param {*} timeout 
	 * @returns 
	 */
	__newWebSocket = (resolve, reject, timeout) => {
		const webSocket = new WebSocket(this._wsUrl)
		webSocket.onopen = event => {
			console.debug('WebSocket opened', event)
			this._webSocket = webSocket
			resolve(webSocket)
		}
		webSocket.onerror = e => {
			if (new Date().getTime() > timeout || this._closeIntended) {
				return reject(e)
			}
			else {
				console.debug('Failed to create WebSocket!', e)
				setTimeout(() => this.__newWebSocket(resolve, reject, timeout), 15000)
			}
		}
	}

	_onMessage = (message, onPause, onResume) => {
		if (message.data === 'ping') {
			this._lastHeartbeat = new Date().getTime()
			this._checkHeratbeat(onPause, onResume)
			// Send pong
			this._webSocket.send('pong')
		}
		else if (message.data === 'close') {
			console.log('Server announced WebSocket will close!')
			if (!this._closeIntended) {
				this._autoReconnect(onPause, onResume)
			}
		}
		else {
			console.debug(message.data)
			const data = JSON.parse(message.data)
			const onMessage = this._subscriptions.get(data.topic)
			if (onMessage) {
				onMessage(data.content)
			}
			else {
				console.debug('No subscription for topic:', data.topic)
			}
		}
	}

	_checkHeratbeat = (onPause, onResume) => {
		clearTimeout(this._checkHeartbeatTimeout)
		this._checkHeartbeatTimeout = setTimeout(() => this._checkHeratbeat(onPause, onResume), 16000)
		if (new Date().getTime() - this._lastHeartbeat > 16000) {
			// Heartbeat dropped
			if (!this._paused) {
				onPause('NO_PING')
				this._paused = true
			}
		}
		else {
			if (this._paused) {
				onResume()
				this._paused = false
			}
		}
	}

	/**
	 * Only one reconnection may be active
	 * @param {*} onPause 
	 * @param {*} onResume 
	 */
	_autoReconnect = (onPause, onResume) => {
		if (!this._reconnecting) {
			this._reconnecting = true
			console.info('Should reconnect...')
			if (!this._closeIntended) {
				const timeout = 253402297199000 // Never: Date = 9999-12-31T23:59:59
				this._connect(timeout, onPause, onResume)
					.then(() => {
						// Reconnected
						console.debug('WebSocket reconnected')
						// Renew subscriptions on server
						if (this._getValidTokenPromise) {
							this._getValidTokenPromise()
								.then(token => {
									this._subscriptions.forEach((callback, topic) => {
										this._webSocket.send(`{"topic": "${topic}", "subscribe": true, "token": "${token}"}`)
									})
								})
						}
						else {
							this._subscriptions.forEach((callback, topic) => {
								this._webSocket.send(`{"topic": "${topic}", "subscribe": true}`)
							})
						}
						this._reconnecting = false
					})
					.catch(e => {
						console.error('Reconnect has given up!', e)
					})
			}
			else {
				this._reconnecting = false
			}
		}
	}

}